import { monadChain } from "@repo/constants";
import { buildStockBrandingImageUrl } from "~/lib/asset-branding";
import { getPlatformApiBaseUrl } from "~/lib/env";
import type {
  AssetModel,
  AssetTaxonomyModel,
  AssetUniverseCanonicalModel,
} from "../data/db/models";

export const CHAIN_LABELS: Readonly<Record<string, string>> = {
  "1": "Ethereum",
  "10": "Optimism",
  "56": "BNB Chain",
  [monadChain.id.toString()]: monadChain.name,
  "137": "Polygon",
  "8453": "Base",
  "42161": "Arbitrum",
  "43114": "Avalanche",
  "98866": "Plume",
  "4663": "Robinhood Chain",
  "1399811149": "Solana",
};

export function getChainLabel(
  chainId: number | string | null | undefined,
): string | null {
  if (chainId == null) {
    return null;
  }
  const key = String(chainId);
  return CHAIN_LABELS[key] ?? null;
}

export interface AssetDisplayInput {
  asset?: AssetModel | null;
  chainId?: number | string | null;
  taxonomy?: AssetTaxonomyModel | null;
}

export interface AssetDisplayIdentityFallback {
  chainId?: number | string | null;
  chainLabel?: string | null;
  imageUrl?: string | null;
  name?: string | null;
  symbol?: string | null;
  tokenAddress?: string | null;
}

export type AssetDisplayIntent = "canonical" | "concrete";

export interface ResolvedAssetDisplayIdentity {
  chainId: number | string | null;
  chainLabel: string | null;
  imageUrl: string | null;
  isResolved: boolean;
  name: string;
  symbol: string;
  tokenAddress: string | null;
}

export function getAssetDisplayName(input: AssetDisplayInput): string | null {
  const taxonomyName =
    nonEmpty(input.taxonomy?.primaryExposure?.name) ??
    nonEmpty(input.taxonomy?.issuer?.name);
  if (taxonomyName) {
    return taxonomyName;
  }

  const baseName = nonEmpty(input.asset?.name) ?? nonEmpty(input.asset?.symbol);
  return chainQualified(baseName, input.chainId ?? input.asset?.chainId);
}

export function getAssetDisplaySymbol(input: AssetDisplayInput): string | null {
  return (
    nonEmpty(input.taxonomy?.primaryExposure?.reference) ??
    nonEmpty(input.asset?.symbol)
  );
}

export function resolveAssetDisplayIdentity(input: {
  asset?: AssetModel | null;
  canonicalAsset?: AssetUniverseCanonicalModel | null;
  fallback?: AssetDisplayIdentityFallback;
  intent?: AssetDisplayIntent;
  preferFallbackIdentity?: boolean;
  taxonomy?: AssetTaxonomyModel | null;
}): ResolvedAssetDisplayIdentity {
  const intent = input.intent;
  const chainId = input.asset?.chainId ?? input.fallback?.chainId ?? null;
  const assetName =
    nonEmpty(input.asset?.name) ?? nonEmpty(input.asset?.symbol);
  const fallbackName =
    nonEmpty(input.fallback?.name) ?? nonEmpty(input.fallback?.symbol);
  const assetSymbol = nonEmpty(input.asset?.symbol);
  const fallbackSymbol =
    nonEmpty(input.fallback?.symbol) ?? nonEmpty(input.fallback?.name);

  if (!intent) {
    const taxonomySymbol = nonEmpty(input.taxonomy?.primaryExposure?.reference);
    const taxonomyName = taxonomySymbol
      ? nonEmpty(input.taxonomy?.primaryExposure?.name)
      : null;
    const issuerName = nonEmpty(input.taxonomy?.issuer?.name);

    return {
      chainId,
      chainLabel:
        getChainLabel(chainId) ?? nonEmpty(input.fallback?.chainLabel) ?? null,
      imageUrl:
        nonEmpty(input.asset?.imageUrl) ??
        nonEmpty(input.fallback?.imageUrl) ??
        null,
      isResolved: Boolean(input.asset || input.taxonomy),
      name:
        taxonomyName ??
        assetName ??
        issuerName ??
        fallbackName ??
        "Unknown asset",
      symbol: taxonomySymbol ?? assetSymbol ?? fallbackSymbol ?? "UNKNOWN",
      tokenAddress:
        nonEmpty(input.asset?.address) ??
        nonEmpty(input.fallback?.tokenAddress) ??
        null,
    };
  }

  const hasFallbackIdentity = fallbackName !== null || fallbackSymbol !== null;
  const concreteName =
    input.preferFallbackIdentity === true
      ? (fallbackName ?? assetName)
      : (assetName ?? fallbackName);
  const concreteSymbol =
    input.preferFallbackIdentity === true
      ? (fallbackSymbol ?? assetSymbol)
      : (assetSymbol ?? fallbackSymbol);
  const canonicalName =
    intent === "canonical" ? nonEmpty(input.canonicalAsset?.name) : null;
  const canonicalSymbol =
    intent === "canonical" ? nonEmpty(input.canonicalAsset?.symbol) : null;
  const taxonomyFallbackName =
    !input.asset && !hasFallbackIdentity
      ? (nonEmpty(input.taxonomy?.primaryExposure?.name) ??
        nonEmpty(input.taxonomy?.issuer?.name))
      : null;
  const taxonomyFallbackSymbol =
    !input.asset && !hasFallbackIdentity
      ? nonEmpty(input.taxonomy?.primaryExposure?.reference)
      : null;
  // A taxonomy row describes exposure and can outlive the query-backed asset
  // metadata row. Keep its text as a settled fallback, but do not present a
  // taxonomy-only concrete identity as fully hydrated while metadata reloads.
  const isResolved =
    intent === "concrete"
      ? Boolean(input.asset)
      : Boolean(input.canonicalAsset || input.asset || input.taxonomy);

  return {
    chainId,
    chainLabel:
      getChainLabel(chainId) ?? nonEmpty(input.fallback?.chainLabel) ?? null,
    imageUrl:
      (intent === "canonical"
        ? nonEmpty(input.canonicalAsset?.imageUrl)
        : null) ??
      nonEmpty(input.asset?.imageUrl) ??
      nonEmpty(input.fallback?.imageUrl) ??
      null,
    isResolved,
    name:
      canonicalName ?? concreteName ?? taxonomyFallbackName ?? "Unknown asset",
    symbol:
      canonicalSymbol ?? concreteSymbol ?? taxonomyFallbackSymbol ?? "UNKNOWN",
    tokenAddress:
      nonEmpty(input.asset?.address) ??
      nonEmpty(input.fallback?.tokenAddress) ??
      null,
  };
}

function chainQualified(
  base: string | null,
  chainId: number | string | null | undefined,
): string | null {
  if (!base) {
    return null;
  }
  const chainLabel = getChainLabel(chainId);
  return chainLabel ? `${base} on ${chainLabel}` : base;
}

/**
 * Artwork policy for surfaces that read the asset domain directly, mirroring
 * `getPortfolioHoldingImageUrl` for the ones with no holdings projection to
 * carry a branding identity. A tokenized equity renders the first-party
 * branding route — which resolves server-side through
 * `asset_metadata_effective` — so a row labelled with a ticker cannot show
 * different art from the pages it links to. Everything else keeps its concrete
 * payload artwork.
 *
 * The wrapped-token payloads this replaces are frequently thumbnail-grade
 * (Defined.fi serves the Mag7 BSC tokens at 25px), which upscales visibly on
 * any retina display.
 */
export function getTokenizedEquityImageUrl(input: {
  imageUrl?: string | null;
  taxonomy?: AssetTaxonomyModel | null;
}): string | null {
  const ticker = input.taxonomy?.isTokenizedEquity
    ? nonEmpty(input.taxonomy.primaryExposure?.reference)
    : null;

  if (!ticker) {
    return input.imageUrl ?? null;
  }

  return buildStockBrandingImageUrl({
    platformApiBaseUrl: getPlatformApiBaseUrl(),
    ticker,
  });
}

function nonEmpty(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}
